import { useState, useEffect, useMemo } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import FilePreview from "../../../components/common/FilePreview";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import { Loader2, FileText, Trash } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setCurrentTab,
  setData,
  setUserInitiatedNav,
} from "../../../store/slices/oldConventionalSlice";
import { MultiSelect } from "../../../components/ui/multiselect";
import upgradation_certificate from "../../../assets/documents/upgradation_certificate.pdf";

const particularsSchema = (alreadyPurchased) =>
  z
    .object({
      already_purchase: z.string().optional(),
      purchase_details: z
        .array(
          z.object({
            loom_id: z.string().optional(),
            attachments: z.array(z.string()).optional(),
            invoice_number: z.string().optional(),
            invoice_date: z.string().optional(),
            invoice_copy: z.any().optional(),
          })
        )
        .optional(),
      purchase_detail_bond: z.any().optional(),
      bank_loan_availed: z.string().optional(),
      dpr_copy: z.any().optional(),
      bank_loan_sanction_letter: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      if (!alreadyPurchased || data.already_purchase === "Yes") {
        if (!data.purchase_details || data.purchase_details.length === 0) {
          ctx.addIssue({
            path: ["purchase_details"],
            code: z.ZodIssueCode.custom,
            message: "Add at least one invoice row",
          });
          return;
        }

        data.purchase_details.forEach((item, index) => {
          if (!item.loom_id || item.loom_id.trim() === "") {
            ctx.addIssue({
              path: ["purchase_details", index, "loom_id"],
              code: z.ZodIssueCode.custom,
              message: "Loom is required",
            });
          }

          const attachments = Array.isArray(item.attachments)
            ? item.attachments
            : [];
          if (attachments.length === 0) {
            ctx.addIssue({
              path: ["purchase_details", index, "attachments"],
              code: z.ZodIssueCode.custom,
              message: "Select at least one component",
            });
          }

          const required = ["invoice_number", "invoice_date"];
          required.forEach((field) => {
            if (
              !item[field] ||
              (typeof item[field] === "string" && item[field].trim() === "")
            ) {
              ctx.addIssue({
                path: ["purchase_details", index, field],
                code: z.ZodIssueCode.custom,
                message: "This field is required",
              });
            }
          });

          if (!item.invoice_copy) {
            ctx.addIssue({
              path: ["purchase_details", index, "invoice_copy"],
              code: z.ZodIssueCode.custom,
              message: "Invoice Copy Required",
            });
          }
        });

        const loomAccessories = {};
        data.purchase_details.forEach((item) => {
          if (item.loom_id) {
            if (!loomAccessories[item.loom_id]) {
              loomAccessories[item.loom_id] = new Set();
            }
            if (Array.isArray(item.attachments)) {
              item.attachments.forEach((att) => {
                const attValue =
                  typeof att === "string" ? att : att?.id || att?.name;
                if (attValue) loomAccessories[item.loom_id].add(attValue);
              });
            }
          }
        });

        Object.entries(loomAccessories).forEach(([loomId, accessories]) => {
          const loom = data.looms_purchase?.find(
            (l) => String(l.id) === loomId
          );
          if (loom && loom.attachments) {
            const requiredAccessories = loom.attachments.map(
              (att) => att.attachment_name
            );
            const missingAccessories = requiredAccessories.filter(
              (req) => !accessories.has(req)
            );

            if (missingAccessories.length > 0) {
              ctx.addIssue({
                path: ["purchase_details"],
                code: z.ZodIssueCode.custom,
                message: `${
                  loom.loom_name
                }: Missing components - ${missingAccessories.join(", ")}`,
              });
            }
          }
        });

        if (!data.bank_loan_availed) {
          ctx.addIssue({
            path: ["bank_loan_availed"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }

        if (
          data.bank_loan_availed === "Yes" &&
          !data.bank_loan_sanction_letter
        ) {
          ctx.addIssue({
            path: ["bank_loan_sanction_letter"],
            code: z.ZodIssueCode.custom,
            message: "Bank Loan Sanction Letter is required",
          });
        }
      }
    });

const PurchasedDetails = ({ already_purchased = false, data = {} }) => {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const location = useLocation();

  const [filePreviews, setFilePreviews] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(particularsSchema(already_purchased)),
    defaultValues: {
      already_purchase: data?.purchase?.already_purchase ?? "",
      purchase_details: data?.purchase?.purchase_details?.length
        ? data.purchase?.purchase_details.map((row) => ({
            loom_id: row.loom_id ? String(row.loom_id) : "",
            attachments: row.attachments ?? [],
            invoice_number: row.invoice_number ?? "",
            invoice_date: row.invoice_date
              ? new Date(row.invoice_date).toISOString().split("T")[0]
              : "",
            invoice_copy: row.invoice_copy ?? "",
          }))
        : [
            {
              loom_id: "",
              attachments: [],
              invoice_number: "",
              invoice_date: "",
              invoice_copy: "",
            },
          ],
      bank_loan_availed: data?.purchase?.bank_loan_availed ?? "",
      dpr_copy: data?.purchase?.dpr_copy ?? undefined,
      purchase_detail_bond:
        data?.purchase?.purchase_details?.purchase_detail_bond ?? undefined,
      bank_loan_sanction_letter:
        data?.purchase?.bank_loan_sanction_letter ?? undefined,
    },
  });

  const fieldVisitRemark = !already_purchased
    ? data?.remarks?.find((remark) => remark.status === "field_visit_completed")
    : null;

  const sanctionedLoomIds =
    fieldVisitRemark?.looms_for_sanction?.map((item) => String(item.loom_id)) ||
    [];

  const applicableLooms = !already_purchased
    ? data?.loom_upgradations?.loom_purchases?.filter((loom) =>
        sanctionedLoomIds.includes(String(loom.id))
      ) || []
    : data?.loom_upgradations?.loom_purchases || [];

  const formData = watch();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "purchase_details",
  });

  const getAvailableAccessories = (currentIndex, currentLoomId) => {
    if (!currentLoomId) return [];

    const selectedLoom = data?.loom_upgradations?.loom_purchases?.find(
      (loom) => String(loom.id) === currentLoomId
    );

    if (!selectedLoom || !selectedLoom.attachments) return [];

    const selectedAccessories = new Set();
    formData.purchase_details.forEach((row, idx) => {
      if (row.loom_id === currentLoomId && idx !== currentIndex) {
        row.attachments.forEach((att) => selectedAccessories.add(att));
      }
    });

    return selectedLoom.attachments
      .map((att) => ({
        id: att.attachment_name,
        name: att.attachment_name,
      }))
      .filter((att) => !selectedAccessories.has(att.id));
  };

  const getRemainingAccessories = (loomId) => {
    if (!loomId) return [];

    const selectedLoom = data?.loom_upgradations?.loom_purchases?.find(
      (loom) => String(loom.id) === loomId
    );

    if (!selectedLoom || !selectedLoom.attachments) return [];

    const allAccessories = selectedLoom.attachments.map(
      (att) => att.attachment_name
    );
    const selectedAccessories = new Set();

    formData.purchase_details.forEach((row) => {
      if (row.loom_id === loomId) {
        row.attachments.forEach((att) => selectedAccessories.add(att));
      }
    });

    return allAccessories.filter((att) => !selectedAccessories.has(att));
  };

  useEffect(() => {
    const previews = {};
    if (data?.purchase?.purchase_details?.length) {
      data?.purchase?.purchase_details.forEach((row, idx) => {
        if (row?.invoice_copy && typeof row.invoice_copy === "string") {
          previews[`invoice_copy_${idx}`] = row.invoice_copy;
        }
      });
    }

    setFilePreviews({
      ...previews,
      dpr_copy: data?.purchase?.dpr_copy ?? undefined,
      purchase_detail_bond: data?.purchase?.purchase_detail_bond ?? undefined,
      bank_loan_sanction_letter:
        data?.purchase?.bank_loan_sanction_letter ?? undefined,
    });
  }, [data]);

  const isValidFile = (file, fieldName) => {
    if (!file) return false;
    const maxSize =
      fieldName === "dpr_copy" ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: `File size should not exceed ${
          fieldName === "dpr_copy" ? "5MB" : "2MB"
        }`,
        variant: "destructive",
      });
      return false;
    }
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Allowed types: JPG, JPEG, PNG, PDF",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleRowFileUpload = async (index, file) => {
    if (!file) return;
    if (!isValidFile(file, "invoice_copy")) return;

    setValue(`purchase_details.${index}.invoice_copy`, file, {
      shouldValidate: true,
    });

    const key = `invoice_copy_${index}`;
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreviews((prev) => ({ ...prev, [key]: e.target.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreviews((prev) => ({ ...prev, [key]: file.name }));
    }

    await trigger(`purchase_details.${index}.invoice_copy`);
  };

  const removeRowFile = (index) => {
    setValue(`purchase_details.${index}.invoice_copy`, undefined, {
      shouldValidate: true,
    });
    setFilePreviews((prev) => ({ ...prev, [`invoice_copy_${index}`]: null }));
  };

  const handleFileUpload = async (fieldName, file) => {
    if (!file) return;
    if (!isValidFile(file, fieldName)) return;

    setValue(fieldName, file, { shouldValidate: true });

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreviews((prev) => ({ ...prev, [fieldName]: e.target.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreviews((prev) => ({ ...prev, [fieldName]: file.name }));
    }

    await trigger(fieldName);
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
    await trigger(fieldName);

    if (fieldName === "bank_loan_availed" && value === "No") {
      handleRemoveFile("bank_loan_sanction_letter");
    }
  };

  const handleLoomChange = (index, loomId) => {
    setValue(`purchase_details.${index}.loom_id`, loomId, {
      shouldValidate: true,
    });
    setValue(`purchase_details.${index}.attachments`, [], {
      shouldValidate: true,
    });
  };

  const onSubmit = async (submitted) => {
    const loomAccessories = {};
    submitted.purchase_details.forEach((item) => {
      if (item.loom_id) {
        if (!loomAccessories[item.loom_id]) {
          loomAccessories[item.loom_id] = new Set();
        }
        if (Array.isArray(item.attachments)) {
          item.attachments.forEach((att) => {
            const attValue =
              typeof att === "string" ? att : att?.id || att?.name;
            if (attValue) loomAccessories[item.loom_id].add(attValue);
          });
        }
      }
    });

    if (
      !already_purchased ||
      (already_purchased && submitted?.already_purchase == "Yes")
    ) {
      const allLoomIds = applicableLooms?.map((l) => String(l.id)) || [];
      const selectedLoomIds = [
        ...new Set(
          submitted.purchase_details.map((item) => item.loom_id).filter(Boolean)
        ),
      ];

      const missingLooms = allLoomIds.filter(
        (loomId) => !selectedLoomIds.includes(loomId)
      );

      if (missingLooms.length > 0) {
        const missingLoomNames = missingLooms.map((loomId) => {
          const loom = data?.loom_upgradations?.loom_purchases?.find(
            (l) => String(l.id) === loomId
          );
          return loom?.loom_name || loomId;
        });

        toast({
          title: "Missing Looms",
          description: `All looms must have invoices. Missing Details for: ${missingLoomNames.join(
            ", "
          )}`,
          variant: "destructive",
        });
        return;
      }
    }

    const incompleteLoomsList = [];
    Object.entries(loomAccessories).forEach(([loomId, accessories]) => {
      const loom = data?.loom_upgradations?.loom_purchases?.find(
        (l) => String(l.id) === loomId
      );
      if (loom && loom.attachments) {
        const requiredAccessories = loom.attachments.map(
          (att) => att.attachment_name
        );
        const missingAccessories = requiredAccessories.filter(
          (req) => !accessories.has(req)
        );

        if (missingAccessories.length > 0) {
          incompleteLoomsList.push({
            loomName: loom.loom_name,
            missing: missingAccessories,
          });
        }
      }
    });

    if (incompleteLoomsList.length > 0) {
      const errorMessage = incompleteLoomsList
        .map(
          (item) =>
            `${item.loomName}: Missing components - ${item.missing.join(", ")}`
        )
        .join("\n");

      toast({
        title: "Incomplete Component Selection",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      submitted.purchase_details?.forEach((row, i) => {
        Object.entries(row).forEach(([key, val]) => {
          if (!val) return;
          if (key === "attachments") {
            if (Array.isArray(val) && val.length > 0) {
              submitData.append(
                `purchase_details[${i}][${key}]`,
                JSON.stringify(val)
              );
            }
            return;
          } else {
            submitData.append(
              `purchase_details[${i}][${key}]`,
              val instanceof File ? val : val
            );
          }
        });
      });

      Object.entries(submitted).forEach(([key, val]) => {
        if (key === "purchase_details") return;
        if (!val) return;
        submitData.append(key, val instanceof File ? val : val);
      });

      if (data?.id) submitData.append("id", data.id);

      const url = already_purchased
        ? "/modernization/purchase_detail"
        : "/modernization/after_purchase_detail";

      const response = await api.post(url, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      dispatch(setData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application saved successfully",
        variant: "success",
      });

      searchParams.delete("tab");
      nav(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );

      dispatch(setUserInitiatedNav(true));
      if (buttonType === "next") {
        if (!already_purchased) {
          dispatch(setCurrentTab("purchase_declaration"));
        } else {
          dispatch(setCurrentTab("declaration"));
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error?.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {already_purchased
              ? "Component Purchase Details"
              : "After Sanction Component Purchase Details"}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6 mt-5">
          {already_purchased && (
            <div className="space-y-2 w-1/2">
              <Label htmlFor="already_purchase">
                Already Purchased <span className="text-red-500">*</span>
              </Label>
              <Controller
                control={control}
                name="already_purchase"
                render={({ field }) => (
                  <Select
                    value={field.value || ""}
                    onValueChange={(v) => {
                      field.onChange(v);
                    }}
                  >
                    <SelectTrigger
                      className={
                        errors.already_purchase ? "border-red-500" : ""
                      }
                    >
                      <SelectValue placeholder="Select Yes or No" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.already_purchase && (
                <p className="text-xs text-red-500">
                  {errors.already_purchase.message}
                </p>
              )}
            </div>
          )}

          {(!already_purchased || formData.already_purchase === "Yes") && (
            <>
              <div className="flex justify-between items-center">
                <h4 className="font-semibold">Invoice Details</h4>
                <Button
                  type="button"
                  onClick={() =>
                    append({
                      loom_id: "",
                      attachments: [],
                      invoice_number: "",
                      invoice_date: "",
                      invoice_copy: undefined,
                    })
                  }
                >
                  + Add Row
                </Button>
              </div>

              {errors.purchase_details &&
                !Array.isArray(errors.purchase_details) && (
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-sm text-red-600">
                      {errors.purchase_details.message}
                    </p>
                  </div>
                )}

              <div className="overflow-x-auto">
                <table className="w-full table-auto border">
                  <thead>
                    <tr className="bg-gray-100 text-center border-b">
                      <th className="p-2 w-[50px]">#</th>
                      <th className="p-2 w-[150px]">Loom Name*</th>
                      <th className="p-2">Components *</th>
                      <th className="p-2">Invoice Number *</th>
                      <th className="p-2">Invoice Date *</th>
                      <th className="p-2 w-[250px]">Invoice Copy *</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {fields.map((field, idx) => {
                      const currentLoomId =
                        formData.purchase_details[idx]?.loom_id;
                      const availableAccessories = getAvailableAccessories(
                        idx,
                        currentLoomId
                      );
                      const remainingAccessories =
                        getRemainingAccessories(currentLoomId);

                      return (
                        <tr
                          key={field.id}
                          className="border-b hover:bg-gray-50 text-center"
                        >
                          <td className="p-3 font-medium align-middle">
                            {idx + 1}
                          </td>

                          <td className="px-4 py-3">
                            <Controller
                              control={control}
                              name={`purchase_details.${idx}.loom_id`}
                              render={({ field }) => (
                                <Select
                                  value={field.value || ""}
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                    handleLoomChange(idx, value);
                                  }}
                                >
                                  <SelectTrigger
                                    className={
                                      errors?.purchase_details?.[idx]?.loom_id
                                        ? "border-red-500"
                                        : ""
                                    }
                                  >
                                    <SelectValue placeholder="Select Loom" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {applicableLooms?.map((loom) => (
                                      <SelectItem
                                        key={loom.id}
                                        value={String(loom.id)}
                                      >
                                        {loom.loom_name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                            {errors?.purchase_details?.[idx]?.loom_id && (
                              <p className="text-xs text-red-500 mt-1">
                                {errors.purchase_details[idx].loom_id.message}
                              </p>
                            )}
                            {currentLoomId &&
                              remainingAccessories.length > 0 && (
                                <p className="text-xs text-orange-500 mt-1">
                                  Remaining: {remainingAccessories.join(", ")}
                                </p>
                              )}
                          </td>

                          <td className="px-4 py-3">
                            <Controller
                              control={control}
                              name={`purchase_details.${idx}.attachments`}
                              render={({ field }) => (
                                <MultiSelect
                                  options={availableAccessories}
                                  value={field.value || []}
                                  onChange={field.onChange}
                                  placeholder="Select Components"
                                  disabled={!currentLoomId}
                                />
                              )}
                            />
                            {errors?.purchase_details?.[idx]?.attachments && (
                              <p className="text-xs text-red-500 mt-1">
                                {
                                  errors.purchase_details[idx].attachments
                                    .message
                                }
                              </p>
                            )}
                          </td>

                          <td className="p-2">
                            <Input
                              type="text"
                              {...register(
                                `purchase_details.${idx}.invoice_number`
                              )}
                              className={
                                errors?.purchase_details?.[idx]?.invoice_number
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {errors?.purchase_details?.[idx]
                              ?.invoice_number && (
                              <p className="text-xs text-red-500">
                                {
                                  errors.purchase_details[idx].invoice_number
                                    .message
                                }
                              </p>
                            )}
                          </td>

                          <td className="p-2">
                            <Input
                              type="date"
                              {...register(
                                `purchase_details.${idx}.invoice_date`
                              )}
                              className={
                                errors?.purchase_details?.[idx]?.invoice_date
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {errors?.purchase_details?.[idx]?.invoice_date && (
                              <p className="text-xs text-red-500">
                                {
                                  errors.purchase_details[idx].invoice_date
                                    .message
                                }
                              </p>
                            )}
                          </td>

                          <td className="p-2">
                            <Input
                              type="file"
                              accept=".jpeg,.jpg,.png,.pdf"
                              onChange={(e) =>
                                handleRowFileUpload(idx, e.target.files?.[0])
                              }
                              className={
                                errors?.purchase_details?.[idx]?.invoice_copy
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            <FilePreview
                              fieldName={`purchase_details.${idx}.invoice_copy`}
                              preview={filePreviews[`invoice_copy_${idx}`]}
                              file={
                                formData.purchase_details?.[idx]
                                  ?.invoice_copy instanceof File
                                  ? formData.purchase_details[idx].invoice_copy
                                  : null
                              }
                              existingFile={
                                typeof formData.purchase_details?.[idx]
                                  ?.invoice_copy === "string"
                                  ? formData.purchase_details[idx].invoice_copy
                                  : null
                              }
                              onRemove={() => removeRowFile(idx)}
                            />
                            {errors?.purchase_details?.[idx]?.invoice_copy && (
                              <p className="text-xs text-red-500">
                                {
                                  errors.purchase_details[idx].invoice_copy
                                    .message
                                }
                              </p>
                            )}
                          </td>

                          <td className="p-2 text-center">
                            {idx > 0 && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => remove(idx)}
                              >
                                <Trash />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Separator />

              {/* DPR copy */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dpr_copy">Detailed Project Report Copy</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="dpr_copy"
                      type="file"
                      accept=".jpeg,.jpg,.png,.pdf"
                      onChange={(e) =>
                        handleFileUpload("dpr_copy", e.target.files?.[0])
                      }
                      className={errors.dpr_copy ? "border-red-500" : ""}
                    />

                    <FilePreview
                      fieldName="dpr_copy"
                      preview={filePreviews.dpr_copy}
                      file={
                        formData.dpr_copy instanceof File
                          ? formData.dpr_copy
                          : null
                      }
                      existingFile={
                        typeof filePreviews.dpr_copy === "string"
                          ? filePreviews.dpr_copy
                          : null
                      }
                      onRemove={() => handleRemoveFile("dpr_copy")}
                    />

                    {errors.dpr_copy && (
                      <p className="text-sm text-red-500 mt-2">
                        {errors.dpr_copy.message}
                      </p>
                    )}
                    <p className="ms-1 text-[12px] text-muted-foreground mt-1">
                      Supported: JPG, JPEG, PNG, PDF. Max 5MB
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_detail_bond">
                    Undertaking certificate for not selling the loom for 5
                    years. <span className="text-red-500">*</span>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() =>
                        handleDownload(
                          upgradation_certificate,
                          `upgradation_certificate.pdf`
                        )
                      }
                      className="text-wrap text-blue-500"
                    >
                      (Download Undertaking Certificate <br /> and print in 20
                      rupees stamp paper )
                    </Button>
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="purchase_detail_bond"
                      type="file"
                      accept=".jpeg,.jpg,.png,.pdf"
                      onChange={(e) =>
                        handleFileUpload(
                          "purchase_detail_bond",
                          e.target.files[0]
                        )
                      }
                      className={
                        errors.purchase_detail_bond ? "border-red-500" : ""
                      }
                    />
                    <FilePreview
                      fieldName="purchase_detail_bond"
                      preview={filePreviews.purchase_detail_bond}
                      file={
                        formData.purchase_detail_bond instanceof File
                          ? formData.purchase_detail_bond
                          : null
                      }
                      existingFile={
                        typeof filePreviews.purchase_detail_bond === "string"
                          ? filePreviews.purchase_detail_bond
                          : null
                      }
                      onRemove={() => handleRemoveFile("purchase_detail_bond")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 5MB)
                    </p>
                  </div>
                  {errors.purchase_detail_bond && (
                    <p className="text-sm text-red-500">
                      {errors.purchase_detail_bond.message}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Bank loan */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_loan_availed">
                    Bank Loan Availed <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="bank_loan_availed"
                    render={({ field }) => (
                      <Select
                        value={field.value || ""}
                        onValueChange={(v) => {
                          field.onChange(v);
                          handleInputChange("bank_loan_availed", v);
                        }}
                      >
                        <SelectTrigger
                          className={
                            errors.bank_loan_availed ? "border-red-500" : ""
                          }
                        >
                          <SelectValue placeholder="Select Yes or No" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.bank_loan_availed && (
                    <p className="text-sm text-red-500">
                      {errors.bank_loan_availed.message}
                    </p>
                  )}
                </div>

                {formData.bank_loan_availed === "Yes" && (
                  <div className="space-y-2">
                    <Label htmlFor="bank_loan_sanction_letter">
                      Bank Loan Sanction Letter{" "}
                      <span className="text-red-500">*</span>
                    </Label>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <Input
                        id="bank_loan_sanction_letter"
                        type="file"
                        accept=".jpeg,.jpg,.png,.pdf"
                        onChange={(e) =>
                          handleFileUpload(
                            "bank_loan_sanction_letter",
                            e.target.files?.[0]
                          )
                        }
                        className={
                          errors.bank_loan_sanction_letter
                            ? "border-red-500"
                            : ""
                        }
                      />

                      <FilePreview
                        fieldName="bank_loan_sanction_letter"
                        preview={filePreviews.bank_loan_sanction_letter}
                        file={
                          formData.bank_loan_sanction_letter instanceof File
                            ? formData.bank_loan_sanction_letter
                            : null
                        }
                        existingFile={
                          typeof filePreviews.bank_loan_sanction_letter ===
                          "string"
                            ? filePreviews.bank_loan_sanction_letter
                            : null
                        }
                        onRemove={() =>
                          handleRemoveFile("bank_loan_sanction_letter")
                        }
                      />

                      {errors.bank_loan_sanction_letter && (
                        <p className="text-sm text-red-500 mt-2">
                          {errors.bank_loan_sanction_letter.message}
                        </p>
                      )}
                      <p className="ms-1 text-[12px] text-muted-foreground mt-1">
                        Supported: JPG, JPEG, PNG, PDF. Max 2MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting && buttonType === "submit" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting && buttonType === "next" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default PurchasedDetails;
